import { api, unwrap, baseURL } from './client';
import { readToken } from '../auth/storage';

// ─────────────────────────────────────────────────────────────────────────────
//  DOCUMENT KEYS & METADATA
// ─────────────────────────────────────────────────────────────────────────────

/** 6 master templates that Superadmin uploads and employees download */
export const COMPANY_TEMPLATE_KEYS = [
  { key: 'ACKNOWLEDGEMENT_FORM', label: 'Acknowledgement Form', desc: 'Company policy acknowledgement terms.' },
  { key: 'BANK_ACCOUNT_DETAILS', label: 'Bank Account Details', desc: 'Salary disbursement account and IFSC form.' },
  { key: 'EMERGENCY_FORM', label: 'Emergency Contact Form', desc: 'Medical information and emergency contacts.' },
  { key: 'POLICIES_HANDBOOK', label: 'Policies Handbook', desc: 'Employee code of conduct and workplace guidelines.' },
  { key: 'UNDERTAKING', label: 'Employment Undertaking', desc: 'Confidentiality and employment undertaking.' },
  { key: 'LEAVE_POLICIES', label: 'Leave Policies', desc: 'Leave guidelines, accrual rules, and policies.' },
];

/** 7 mandatory documents employees must upload before final submission */
export const USER_UPLOAD_DOCS = [
  { key: 'ACKNOWLEDGEMENT_FORM', label: 'Signed Acknowledgement Form', required: true, accept: '.pdf,.jpg,.jpeg,.png' },
  { key: 'BANK_ACCOUNT_DETAILS', label: 'Bank Details', required: true, accept: '.pdf,.jpg,.jpeg,.png' },
  { key: 'EMERGENCY_FORM', label: 'Filled Emergency Form', required: true, accept: '.pdf,.jpg,.jpeg,.png' },
  { key: 'UNDERTAKING', label: 'Signed Undertaking', required: true, accept: '.pdf,.jpg,.jpeg,.png' },
  { key: 'LEAVE_POLICIES', label: 'Leave Policy Acknowledgement', required: true, accept: '.pdf,.jpg,.jpeg,.png' },
  { key: 'AADHAR_COPY', label: 'Aadhaar Card Copy', required: true, accept: '.pdf,.jpg,.jpeg,.png' },
  { key: 'PAN_CARD_COPY', label: 'PAN Card Copy', required: true, accept: '.pdf,.jpg,.jpeg,.png' },
];

// ─────────────────────────────────────────────────────────────────────────────
//  HELPER — Authenticated Blob Download
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Performs an authenticated fetch, converts to Blob, then triggers browser download.
 * Uses fetch (not window.open) so the Bearer JWT token is always attached.
 */
async function authenticatedBlobDownload(url, fallbackFilename) {
  const token = readToken();
  const userId = typeof localStorage !== 'undefined' ? localStorage.getItem('userId') : null;
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (userId) headers['X-User-ID'] = userId;

  const res = await fetch(url, { headers });
  if (!res.ok) {
    let errorMsg = `Download failed (${res.status})`;
    try {
      const errText = await res.text();
      const errJson = JSON.parse(errText);
      if (errJson?.message) errorMsg = errJson.message;
      else if (errJson?.error) errorMsg = errJson.error;
    } catch (_) {}
    throw new Error(errorMsg);
  }

  const blob = await res.blob();
  const disposition = res.headers.get('content-disposition') || '';
  const match = /filename="?([^"]+)"?/.exec(disposition);
  const filename = match?.[1] || fallbackFilename;
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => {
    URL.revokeObjectURL(objectUrl);
  }, 30000);
}

// ─────────────────────────────────────────────────────────────────────────────
//  TEMPLATE ENDPOINTS (Superadmin manages, everyone downloads)
// ─────────────────────────────────────────────────────────────────────────────

/** GET /api/onboarding/templates — Returns list of uploaded master templates */
export async function getCompanyTemplates() {
  try {
    const res = await api.get('/onboarding/templates');
    return unwrap(res) || [];
  } catch (err) {
    console.warn('[onboarding] getCompanyTemplates failed:', err.message);
    return [];
  }
}

/** POST /api/onboarding/templates/upload — Superadmin uploads a master template */
export async function uploadCompanyTemplate({ docKey, title, description, file }) {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('docKey', docKey);
  fd.append('title', title);
  if (description) fd.append('description', description);
  const res = await api.post('/onboarding/templates/upload', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return unwrap(res);
}

/** PUT /api/onboarding/templates/{docKey} — Superadmin replaces an existing template */
export async function replaceCompanyTemplate({ docKey, title, description, file }) {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('title', title);
  if (description) fd.append('description', description);
  const res = await api.put(`/onboarding/templates/${docKey}`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return unwrap(res);
}

/** DELETE /api/onboarding/templates/{docKey} — Superadmin deletes a master template */
export async function deleteCompanyTemplate(docKey) {
  const res = await api.delete(`/onboarding/templates/${docKey}`);
  return unwrap(res);
}

/**
 * GET /api/onboarding/templates/download/{docKey}
 * Streams file bytes from S3 via authenticated blob download.
 */
export async function downloadCompanyTemplate(docKey, fallbackFilename) {
  await authenticatedBlobDownload(
    `${baseURL}/onboarding/templates/download/${docKey}`,
    fallbackFilename || `${docKey}.pdf`
  );
}

/**
 * GET /api/onboarding/templates/download-all
 * Downloads all uploaded master templates packaged in a single ZIP archive.
 */
export async function downloadAllCompanyTemplatesZip() {
  await authenticatedBlobDownload(
    `${baseURL}/onboarding/templates/download-all`,
    'company_onboarding_templates.zip'
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  USER ONBOARDING ENDPOINTS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * GET /api/onboarding/me
 * Returns current user's onboarding record:
 *   { status, termsAccepted, submittedAt, docsMetadata: { [docKey]: {...} } }
 */
export async function getMyOnboarding() {
  try {
    const res = await api.get('/onboarding/me');
    return unwrap(res);
  } catch (err) {
    console.warn('[onboarding] getMyOnboarding failed:', err.message);
    return { status: 'PENDING', termsAccepted: false, docsMetadata: {} };
  }
}

/**
 * POST /api/onboarding/upload-doc (multipart: docKey, file)
 * Uploads one user document to S3 and saves metadata in JSON column.
 */
export async function uploadUserDoc({ docKey, file }) {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('docKey', docKey);
  const res = await api.post('/onboarding/upload-doc', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return unwrap(res);
}

/**
 * DELETE /api/onboarding/doc/{docKey}
 * Deletes user's draft for the given doc from S3 + metadata.
 */
export async function deleteUserDoc(docKey) {
  const res = await api.delete(`/onboarding/doc/${docKey}`);
  return unwrap(res);
}

/**
 * GET /api/onboarding/download-doc/{docKey}
 * Downloads user's own uploaded document via authenticated blob stream.
 */
export async function downloadMyDoc(docKey, fallbackFilename) {
  await authenticatedBlobDownload(
    `${baseURL}/onboarding/download-doc/${docKey}`,
    fallbackFilename || `${docKey}.pdf`
  );
}

/**
 * GET /api/onboarding/download-all-my-docs
 * Downloads all uploaded documents of the current user packaged in a single ZIP.
 */
export async function downloadAllMyDocsZip(fallbackFilename) {
  await authenticatedBlobDownload(
    `${baseURL}/onboarding/download-all-my-docs`,
    fallbackFilename || 'my_onboarding_documents.zip'
  );
}

/**
 * POST /api/onboarding/save-draft  { termsAccepted }
 * Persists draft state without submitting.
 */
export async function saveDraft({ termsAccepted }) {
  const res = await api.post('/onboarding/save-draft', { termsAccepted });
  return unwrap(res);
}

/**
 * POST /api/onboarding/submit  { termsAccepted }
 * Final submission — all 7 docs required + terms must be true.
 */
export async function submitFinalOnboarding({ termsAccepted }) {
  const res = await api.post('/onboarding/submit', { termsAccepted });
  return unwrap(res);
}

// ─────────────────────────────────────────────────────────────────────────────
//  SUPERADMIN REVIEW ENDPOINTS
// ─────────────────────────────────────────────────────────────────────────────

/** GET /api/onboarding/admin/users — All employees' onboarding status list */
export async function adminListOnboardingUsers() {
  const res = await api.get('/onboarding/admin/users');
  return unwrap(res) || [];
}

/** GET /api/onboarding/admin/users/{userId}/download/{docKey} */
export async function adminDownloadUserDoc(userId, docKey, fallbackFilename) {
  await authenticatedBlobDownload(
    `${baseURL}/onboarding/admin/users/${userId}/download/${docKey}`,
    fallbackFilename || `${docKey}_user${userId}.pdf`
  );
}

/**
 * GET /api/onboarding/admin/users/{userId}/download-all
 * Superadmin downloads all of an employee's uploaded documents as a ZIP archive.
 */
export async function adminDownloadUserDocsZip(userId, fallbackFilename) {
  await authenticatedBlobDownload(
    `${baseURL}/onboarding/admin/users/${userId}/download-all`,
    fallbackFilename || `employee_${userId}_onboarding_documents.zip`
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  DOCUMENT PREVIEW HELPERS (PDF / IMAGE)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetches an authenticated file blob and generates an object URL for inline viewing
 * (renders in <iframe> for PDF or <img> for images).
 */
export async function fetchDocBlob(url) {
  const token = readToken();
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`Failed to load document (${res.status})`);
  const blob = await res.blob();
  const contentType = res.headers.get('content-type') || blob.type || 'application/pdf';
  const objectUrl = URL.createObjectURL(blob);
  return { objectUrl, contentType };
}

/** Previews employee's own uploaded document */
export async function viewMyDoc(docKey) {
  return fetchDocBlob(`${baseURL}/onboarding/download-doc/${docKey}`);
}

/** Previews master company template */
export async function viewCompanyTemplate(docKey) {
  return fetchDocBlob(`${baseURL}/onboarding/templates/download/${docKey}`);
}

/** Superadmin previews employee's uploaded document */
export async function adminViewUserDoc(userId, docKey) {
  return fetchDocBlob(`${baseURL}/onboarding/admin/users/${userId}/download/${docKey}`);
}
